import { useState, useEffect, useCallback, useRef } from 'react';
import {
  apiClient,
  SoakTestConfig,
  SoakTestStatus,
  SoakLog,
  SoakLogSeverity,
  SoakLogSource,
} from '../api/client';
import { MetricCardSkeleton, CardSkeleton } from '../components/Skeleton';
import { toast } from '../components/Toast';

const DURATION_PRESETS = [
  { label: '1 hour', value: 1 },
  { label: '4 hours', value: 4 },
  { label: '8 hours', value: 8 },
  { label: '24 hours', value: 24 },
  { label: 'Custom', value: -1 },
];

const SEVERITY_OPTIONS: SoakLogSeverity[] = ['ALL', 'INFO', 'WARN', 'ERROR', 'CRITICAL'];
const SOURCE_OPTIONS: SoakLogSource[] = ['all', 'orchestrator', 'android', 'cloudflare'];

const SEVERITY_COLORS: Record<string, string> = {
  INFO: '#757575',
  WARN: '#ff9800',
  ERROR: '#f44336',
  CRITICAL: '#d32f2f',
};

export function SoakTestPage() {
  // Status state
  const [status, setStatus] = useState<SoakTestStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Configuration state
  const [config, setConfig] = useState<SoakTestConfig>({
    duration_hours: 1,
    audio_interval_minutes: 10,
    channels: ['general'],
    environment: 'development',
  });
  const [durationPreset, setDurationPreset] = useState<number>(1);
  const [customDuration, setCustomDuration] = useState<number>(1);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Logs state
  const [logs, setLogs] = useState<SoakLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logSeverity, setLogSeverity] = useState<SoakLogSeverity>('ALL');
  const [logSource, setLogSource] = useState<SoakLogSource>('all');
  const [logSearch, setLogSearch] = useState('');
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Expanded device row
  const [expandedDevice, setExpandedDevice] = useState<string | null>(null);

  // Refs
  const logContainerRef = useRef<HTMLDivElement>(null);
  const statusIntervalRef = useRef<number | null>(null);
  const logsIntervalRef = useRef<number | null>(null);

  // Load status
  const loadStatus = useCallback(async () => {
    try {
      const response = await apiClient.getSoakTestStatus();
      if (response.success && response.data) {
        // Normalize API response to match expected SoakTestStatus interface
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rawData = response.data as any;
        const normalizedStatus: SoakTestStatus = {
          test_id: rawData.test_id || null,
          status: rawData.status || 'idle',
          started_at: rawData.started_at || null,
          duration_hours: rawData.duration_hours || 0,
          elapsed_ms: rawData.elapsed_ms || 0,
          metrics: {
            devices_active: rawData.metrics?.devices_active || 0,
            transcripts_sent: rawData.metrics?.audio_chunks_sent || rawData.metrics?.transcripts_sent || 0,
            transcripts_received: rawData.metrics?.transcripts_received || 0,
            avg_latency_ms: rawData.metrics?.avg_latency_ms || 0,
            errors: rawData.metrics?.error_count || rawData.metrics?.errors || 0,
            crashes: rawData.metrics?.crashes || 0,
            missing_transcripts: rawData.metrics?.transcripts_missing || rawData.metrics?.missing_transcripts || 0,
          },
          devices: rawData.devices || [],
        };
        setStatus(normalizedStatus);
      }
    } catch {
      // Silent fail for polling
    }
  }, []);

  // Load logs
  const loadLogs = useCallback(async () => {
    if (!status?.test_id) return;

    setIsLoadingLogs(true);
    try {
      const response = await apiClient.getSoakLogs({
        test_id: status.test_id,
        limit: 200,
        severity: logSeverity,
        source: logSource,
        search: logSearch || undefined,
      });
      if (response.success && response.data) {
        setLogs(response.data.logs);
        setLogsTotal(response.data.total);

        // Auto-scroll to bottom
        if (isAutoScroll && logContainerRef.current) {
          setTimeout(() => {
            if (logContainerRef.current) {
              logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
            }
          }, 0);
        }
      }
    } catch {
      // Silent fail for polling
    }
    setIsLoadingLogs(false);
  }, [status?.test_id, logSeverity, logSource, logSearch, isAutoScroll]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setError(null);
      try {
        await loadStatus();
      } catch {
        setError('Failed to load soak test status');
        toast.error('Failed to load soak test status');
      }
      setIsLoading(false);
    };
    init();
  }, [loadStatus]);

  // Auto-refresh status every 10 seconds
  useEffect(() => {
    statusIntervalRef.current = window.setInterval(loadStatus, 10000);
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, [loadStatus]);

  // Auto-refresh logs every 5 seconds when running
  useEffect(() => {
    if (status?.status === 'running' && status.test_id) {
      loadLogs();
      logsIntervalRef.current = window.setInterval(loadLogs, 5000);
    }
    return () => {
      if (logsIntervalRef.current) {
        clearInterval(logsIntervalRef.current);
      }
    };
  }, [status?.status, status?.test_id, loadLogs]);

  // Reload logs when filters change
  useEffect(() => {
    if (status?.test_id) {
      loadLogs();
    }
  }, [logSeverity, logSource, logSearch, status?.test_id, loadLogs]);

  // Handle start test
  const handleStartTest = async () => {
    setIsStarting(true);
    setError(null);

    const duration = durationPreset === -1 ? customDuration : durationPreset;
    const testConfig: SoakTestConfig = {
      ...config,
      duration_hours: duration,
    };

    try {
      const response = await apiClient.startSoakTest(testConfig);
      if (response.success && response.data) {
        toast.success(`Soak test started: ${response.data.test_id}`);
        await loadStatus();
      } else {
        setError(response.error || 'Failed to start soak test');
        toast.error(response.error || 'Failed to start soak test');
      }
    } catch {
      setError('Failed to start soak test');
      toast.error('Failed to start soak test');
    }
    setIsStarting(false);
  };

  // Handle stop test
  const handleStopTest = async () => {
    setIsStopping(true);
    try {
      const response = await apiClient.stopSoakTest();
      if (response.success) {
        toast.success('Soak test stopped');
        await loadStatus();
      } else {
        toast.error(response.error || 'Failed to stop soak test');
      }
    } catch {
      toast.error('Failed to stop soak test');
    }
    setIsStopping(false);
  };

  // Handle download logs
  const handleDownloadLogs = async () => {
    if (!status?.test_id) return;

    try {
      const blob = await apiClient.downloadSoakLogs(status.test_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soak-test-${status.test_id}-logs.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Logs downloaded');
    } catch {
      toast.error('Failed to download logs');
    }
  };

  // Format elapsed time
  const formatElapsedTime = (ms: number): string => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format timestamp for logs
  const formatLogTimestamp = (ts: number): string => {
    return new Date(ts).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Format last seen
  const formatLastSeen = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Get status badge color
  const getStatusColor = (testStatus: string): string => {
    switch (testStatus) {
      case 'running':
        return 'var(--primary)';
      case 'completed':
        return 'var(--success)';
      case 'failed':
        return 'var(--error)';
      case 'stopped':
        return 'var(--warning)';
      case 'idle':
      default:
        return 'var(--text-secondary)';
    }
  };

  // Get device status color
  const getDeviceStatusColor = (deviceStatus: string): string => {
    switch (deviceStatus) {
      case 'healthy':
        return 'var(--success)';
      case 'warning':
        return 'var(--warning)';
      case 'error':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  if (isLoading) {
    return (
      <div className="soak-test-page">
        <h1 className="page-title">Soak Test Control</h1>
        <div className="metrics-grid">
          {[1, 2, 3, 4].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <CardSkeleton lines={4} />
        <CardSkeleton lines={6} />
      </div>
    );
  }

  const isRunning = status?.status === 'running';
  const showConfig = !isRunning; // Show config for idle, stopped, completed, failed, or null status

  return (
    <div className="soak-test-page">
      {/* Header */}
      <div className="page-header soak-header">
        <h1 className="page-title">Soak Test Control</h1>
        <div className="soak-header-actions">
          <div
            className="soak-status-badge"
            style={{ backgroundColor: getStatusColor(status?.status || 'idle') }}
          >
            {status?.status?.toUpperCase() || 'NOT RUNNING'}
          </div>
          {isRunning && (
            <div className="soak-elapsed">
              <span className="soak-elapsed-label">Elapsed:</span>
              <span className="soak-elapsed-time">{formatElapsedTime(status?.elapsed_ms || 0)}</span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="card error-card">{error}</div>}

      {/* Configuration Panel */}
      {showConfig && (
        <div className="card">
          <h2 className="section-title">Test Configuration</h2>

          <div className="soak-config-grid">
            {/* Duration */}
            <div className="form-group">
              <label>Duration</label>
              <div className="duration-selector">
                {DURATION_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    className={`duration-btn ${durationPreset === preset.value ? 'active' : ''}`}
                    onClick={() => setDurationPreset(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              {durationPreset === -1 && (
                <div className="custom-duration">
                  <input
                    type="number"
                    className="input"
                    value={customDuration}
                    onChange={(e) => setCustomDuration(parseInt(e.target.value, 10) || 1)}
                    min={1}
                    max={168}
                  />
                  <span>hours</span>
                </div>
              )}
            </div>

            {/* Audio Interval */}
            <div className="form-group">
              <label>Audio Interval</label>
              <p className="form-hint">How often to send test audio</p>
              <div className="interval-input">
                <input
                  type="number"
                  className="input"
                  value={config.audio_interval_minutes}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      audio_interval_minutes: parseInt(e.target.value, 10) || 10,
                    }))
                  }
                  min={1}
                  max={60}
                />
                <span>minutes</span>
              </div>
            </div>

            {/* Environment */}
            <div className="form-group">
              <label>Environment</label>
              <div className="environment-selector">
                <button className="env-btn active">Development</button>
                <button className="env-btn" disabled title="Coming soon">
                  Staging
                </button>
                <button className="env-btn" disabled title="Coming soon">
                  Production
                </button>
              </div>
            </div>
          </div>

          <div className="soak-start-section">
            <button className="button soak-start-btn" onClick={handleStartTest} disabled={isStarting}>
              {isStarting ? 'Starting...' : 'Start Soak Test'}
            </button>
          </div>
        </div>
      )}

      {/* Live Metrics Dashboard */}
      {isRunning && status && (
        <div className="card">
          <div className="section-header">
            <h2 className="section-title">Live Metrics</h2>
            <button className="button button-secondary soak-stop-btn" onClick={handleStopTest} disabled={isStopping}>
              {isStopping ? 'Stopping...' : 'Stop Test'}
            </button>
          </div>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-value">{status.metrics.devices_active}</span>
              <span className="metric-label">Active Devices</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.transcripts_sent}</span>
              <span className="metric-label">Transcripts Sent</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.transcripts_received}</span>
              <span className="metric-label">Transcripts Received</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.avg_latency_ms.toFixed(0)}ms</span>
              <span className="metric-label">Avg Latency</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${status.metrics.errors > 0 ? 'metric-warning' : ''}`}>
                {status.metrics.errors}
              </span>
              <span className="metric-label">Errors</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${status.metrics.crashes > 0 ? 'metric-warning' : ''}`}>
                {status.metrics.crashes}
              </span>
              <span className="metric-label">Crashes</span>
            </div>
            <div className="metric-card">
              <span
                className={`metric-value ${status.metrics.missing_transcripts > 0 ? 'metric-warning' : ''}`}
              >
                {status.metrics.missing_transcripts}
              </span>
              <span className="metric-label">Missing</span>
            </div>
          </div>
        </div>
      )}

      {/* Device Status Table */}
      {isRunning && status && status.devices.length > 0 && (
        <div className="card">
          <h2 className="section-title">Device Status</h2>
          <div className="soak-device-list">
            <div className="soak-device-header">
              <span>Device ID</span>
              <span>Status</span>
              <span>Battery</span>
              <span>Screen</span>
              <span>Last Seen</span>
              <span>Transcripts</span>
            </div>
            {status.devices.map((device) => (
              <div key={device.device_id}>
                <div
                  className={`soak-device-row ${expandedDevice === device.device_id ? 'expanded' : ''}`}
                  onClick={() =>
                    setExpandedDevice(expandedDevice === device.device_id ? null : device.device_id)
                  }
                >
                  <span className="device-id">{device.device_id.slice(0, 8)}...</span>
                  <span className="device-status" style={{ color: getDeviceStatusColor(device.status) }}>
                    {device.status}
                  </span>
                  <span className={`battery-level ${device.battery_level < 20 ? 'low' : ''}`}>
                    {device.battery_level}%
                    {device.is_plugged_in && ' ⚡'}
                  </span>
                  <span>{device.is_screen_on ? 'On' : 'Off'}</span>
                  <span>{formatLastSeen(device.last_seen)}</span>
                  <span>{device.transcripts_received}</span>
                </div>
                {expandedDevice === device.device_id && (
                  <div className="soak-device-details">
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Full Device ID</span>
                        <span className="detail-value detail-mono">{device.device_id}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Charging</span>
                        <span className="detail-value">{device.is_plugged_in ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Battery Level</span>
                        <span className="detail-value">{device.battery_level}%</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Screen State</span>
                        <span className="detail-value">{device.is_screen_on ? 'On' : 'Off'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log Viewer */}
      {(isRunning || status?.status === 'completed' || status?.status === 'failed') && status?.test_id && (
        <div className="card soak-logs-card">
          <div className="section-header">
            <h2 className="section-title">Logs ({logsTotal})</h2>
            <div className="log-actions">
              <button
                className={`button button-small ${isAutoScroll ? 'active' : 'button-secondary'}`}
                onClick={() => setIsAutoScroll(!isAutoScroll)}
              >
                {isAutoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
              </button>
              <button className="button button-small button-secondary" onClick={handleDownloadLogs}>
                Download Logs
              </button>
            </div>
          </div>

          {/* Log Filters */}
          <div className="log-filters">
            <div className="log-filter-group">
              <label>Severity:</label>
              <select
                className="input log-filter-select"
                value={logSeverity}
                onChange={(e) => setLogSeverity(e.target.value as SoakLogSeverity)}
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="log-filter-group">
              <label>Source:</label>
              <select
                className="input log-filter-select"
                value={logSource}
                onChange={(e) => setLogSource(e.target.value as SoakLogSource)}
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="log-filter-group log-search-group">
              <label>Search:</label>
              <input
                type="text"
                className="input"
                placeholder="Search logs..."
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Log Viewer */}
          <div className="log-viewer" ref={logContainerRef}>
            {isLoadingLogs && logs.length === 0 ? (
              <div className="log-loading">Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className="log-empty">No logs found</div>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="log-entry" style={{ borderLeftColor: SEVERITY_COLORS[log.severity] }}>
                  <span className="log-timestamp">[{formatLogTimestamp(log.timestamp)}]</span>
                  <span className="log-severity" style={{ color: SEVERITY_COLORS[log.severity] }}>
                    [{log.severity}]
                  </span>
                  <span className="log-source">[{log.source}]</span>
                  <span className="log-message">{log.message}</span>
                  {log.device_id && <span className="log-device">({log.device_id.slice(0, 8)})</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Results Summary (for completed/failed tests) */}
      {(status?.status === 'completed' || status?.status === 'failed') && status?.metrics && (
        <div className="card">
          <h2 className="section-title">Test Results</h2>
          <div className={`soak-result-banner ${status.status}`}>
            {status.status === 'completed' ? 'Test Completed Successfully' : 'Test Failed'}
          </div>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-value">{formatElapsedTime(status.elapsed_ms)}</span>
              <span className="metric-label">Total Duration</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.devices_active}</span>
              <span className="metric-label">Devices Tested</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">
                {status.metrics.transcripts_sent > 0
                  ? ((status.metrics.transcripts_received / status.metrics.transcripts_sent) * 100).toFixed(1)
                  : 0}
                %
              </span>
              <span className="metric-label">Delivery Rate</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.avg_latency_ms.toFixed(0)}ms</span>
              <span className="metric-label">Avg Latency</span>
            </div>
          </div>
          <div className="soak-new-test">
            <button className="button" onClick={() => loadStatus()}>
              Start New Test
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
